import React from "react";
import { useHistory } from "react-router-dom";
import { Report, Tag, Traveler } from "types";
import { CustomReport } from "../CustomReport";
import { TextFilterParams } from "../ReportFilterState";
import { ReportColumnDef, ReportColumnFilterValueType } from "../SuggestedReports";

export const CustomersReport = ({ report }: { report: Report }) => {
    const columns = React.useMemo(() => getColumnDefs({ report }), [report]);
    return <CustomReport dateFields={["createdAt", "updated"]} report={report} columns={columns} />;
};

interface GetColumnDefsParams {
    report: Report;
}

const CustomerIdRenderer = ({ value }: { value: string }) => {
    const history = useHistory();
    const handleClick = () => history.push(`/customers/contacts/${value}`);
    return (
        <div className="hover:cursor-pointer hover:text-blue hover:underline" onClick={handleClick}>
            {value}
        </div>
    );
};

const getColumnDefs = ({ report }: GetColumnDefsParams): ReportColumnDef[] => [
    {
        field: "id",
        headerName: "Customer ID",
        type: "objectid",
        cellRenderer: CustomerIdRenderer,
    },
    {
        field: "name",
        headerName: "Name",
        minWidth: 200,
        cellClass: "no-translate",
        filterValueType: ReportColumnFilterValueType.STRING,
        filter: "agTextColumnFilter",
        filterParams: TextFilterParams,
    },
    {
        field: "isOrganizer",
        headerName: "Is Organizer",
        minWidth: 120,
        valueGetter: ({ data }: { data: Traveler }) => data?.isOrganizer ?? false,
        valueFormatter: ({ value }: { value: boolean }) => (value ? "Yes" : "No"),
        filter: "agSetColumnFilter",
        filterParams: {
            values: [true, false],
            valueFormatter: ({ value }: { value: boolean }) => (value ? "Yes" : "No"),
            suppressMiniFilter: true,
        },
    },
    {
        field: "email",
        headerName: "Email",
        minWidth: 250,
        cellClass: "no-translate",
        filterValueType: ReportColumnFilterValueType.STRING,
        filter: "agTextColumnFilter",
        filterParams: TextFilterParams,
    },
    {
        field: "additionalEmails",
        headerName: "Other Emails",
        minWidth: 250,
        cellClass: "no-translate",
        valueGetter: ({ data }: { data: Traveler }) => {
            return data?.additionalEmails?.join(", ") ?? "";
        },
        filter: false,
    },
    {
        field: "additionalNames",
        headerName: "Other Names",
        minWidth: 200,
        cellClass: "no-translate",
        valueGetter: ({ data }: { data: Traveler }) => {
            return data?.additionalNames?.join(", ") ?? "";
        },
        filter: false,
    },
    {
        field: "phone",
        headerName: "Phone",
        minWidth: 150,
        cellClass: "no-translate",
        filterValueType: ReportColumnFilterValueType.STRING,
        filter: "agTextColumnFilter",
        filterParams: TextFilterParams,
    },
    {
        field: "additionalPhones",
        headerName: "Other Phone Numbers",
        minWidth: 200,
        cellClass: "no-translate",
        valueGetter: ({ data }: { data: Traveler }) => {
            return data?.additionalPhones?.join(", ") ?? "";
        },
        filter: false,
    },
    {
        field: "lifetimeValue",
        headerName: "Lifetime Value",
        type: "currency",
        valueGetter: ({ data }: { data: Traveler }) => {
            return data?.lifetimeValue ?? 0;
        },
        filterParams: {
            maxNumConditions: 1,
        },
    },
    {
        field: "data.howDidYouHearAboutUs",
        headerName: "How did you hear about us?",
        minWidth: 250,
        cellClass: "no-translate",
        valueGetter: ({ data }: { data: Traveler }) => {
            return data?.data?.["How did you hear about us?"] ?? "";
        },
        filter: false,
    },
    {
        field: "createdAt",
        headerName: "Created Date",
        headerValueGetter: () => "Created At",
        type: "datetime",
        filterParams: {
            report,
        },
        filterValueType: ReportColumnFilterValueType.DATE,
    },
    {
        field: "updated",
        headerName: "Updated Date",
        type: "datetime",
        filterParams: {
            report,
        },
        dateComponent: ReportColumnFilterValueType.DATE,
        filterValueType: ReportColumnFilterValueType.DATE,
    },
    {
        field: "tags._id",
        headerName: "Tag(s)",
        valueGetter: ({ data }) => {
            return data?.tags?.map((tag: Tag) => tag.id) || [];
        },
        filter: false,
    },
    {
        field: "marketingConsents",
        headerName: "Marketing Consent",
        minWidth: 250,
        cellClass: "no-translate",
        valueGetter: ({ data }: { data: Traveler }) => {
            if (!data?.marketingConsents || data.marketingConsents.length === 0) return "";
            return data.marketingConsents[0].value;
        },
        filter: false,
    },
];
